import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

interface Params {
  readonly onSuccess: () => void;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
}

interface FormCallbacks {
  readonly setFormError: (errors: readonly string[] | null) => unknown;
  readonly setSubmitting: (isSubmitting: boolean) => void;
  readonly successMessage: string;
}

export const useDirectMutationCallbacks = ({
  onSuccess,
  queryKeysToInvalidate,
}: Readonly<Params>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(
    ({
      setFormError,
      setSubmitting,
      successMessage,
    }: Readonly<FormCallbacks>) => ({
      onError: (error: unknown) => {
        setFormError(userFriendlyErrors(error as BackError));
        setSubmitting(false);
      },
      onSuccess: () => {
        queryKeysToInvalidate.forEach((queryKey) => {
          void queryClient.invalidateQueries({ queryKey });
        });
        enqueueSnackbar(successMessage);
        setSubmitting(false);
        onSuccess();
      },
    }),
    [enqueueSnackbar, onSuccess, queryClient, queryKeysToInvalidate],
  );
};
